import {useEffect, useState} from "react";
import {Users, Package, Flower2, ShoppingBag, PhoneCall, Check, Home as HomeIcon, Gift, Plane, FileText, Globe2} from "lucide-react";
import Logo from "@/components/Logo";
import AuthModal from "@/pages/modals/Auth";
import {apiGet, apiPost} from "@/lib/api";
import {useSite, Rich} from "@/lib/site";

const POINTS = [
  [Flower2, "Culture", "Panchang, choghadiya, rashifal, scriptures and bhajans"],
  [Users, "Community", "Join a samaj and ask anything about Gujarati life"],
  [ShoppingBag, "Commerce", "Authentic, evaluated Gujarati products and services"],
  [Package, "Personal Concierge", "Your lifestyle and household manager"],
];
const HELP = [
  [HomeIcon, "Home & household", "Staff, repairs, bills, groceries and upkeep — wherever your home is."],
  [Gift, "Shopping & gifting", "Find, verify and send the right thing, from anywhere in the world."],
  [Plane, "Travel & occasions", "Bookings, family events, weddings and celebrations, handled end to end."],
  [FileText, "Errands & paperwork", "Renewals, appointments and the follow-ups nobody has time for."],
];

function ConciergeForm({notify}) {
  const [plans, setPlans] = useState([]);
  const [f, setF] = useState({name: "", email: "", phone: "", city: "", plan_id: "premium", needs: ""});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { apiGet("/concierge/plans").then(setPlans).catch(() => {}); }, []);
  const ch = e => setF({...f, [e.target.name]: e.target.value});
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try { await apiPost("/public/concierge", f); setDone(true); notify("Thank you! Your concierge will reach out shortly"); }
    catch (err) { notify(err?.response?.data?.detail || "Please check the form and try again"); }
    finally { setBusy(false); }
  };
  if (done) return <div className="gfi-form pc-done" data-testid="public-concierge-done"><h3>Thank you, {f.name.split(" ")[0]}!</h3><p>Your request reached our concierge team. We'll call or email you soon.</p></div>;
  return (
    <form className="gfi-form" onSubmit={submit} data-testid="public-concierge-form">
      <div className="eyebrow">TALK TO YOUR CONCIERGE · NO ACCOUNT NEEDED</div>
      <div className="pc-plans" data-testid="public-concierge-plans">
        {plans.filter(p => p.plan_id !== "purchase_report").map(p => (
          <button type="button" key={p.plan_id} data-testid={`public-plan-${p.plan_id}`} className={f.plan_id === p.plan_id ? "pc-plan active" : "pc-plan"} onClick={() => setF({...f, plan_id: p.plan_id})}>
            {f.plan_id === p.plan_id && <Check size={12}/>}<strong>{p.name}</strong><small>{p.blurb}</small>
          </button>
        ))}
      </div>
      <label>Name<input data-testid="pc-name" required minLength={2} name="name" value={f.name} onChange={ch}/></label>
      <div className="form-row">
        <label>Email<input data-testid="pc-email" required type="email" name="email" value={f.email} onChange={ch}/></label>
        <label>Phone / WhatsApp<input data-testid="pc-phone" required minLength={6} type="tel" name="phone" value={f.phone} onChange={ch}/></label>
      </div>
      <label>City, country<input data-testid="pc-city" name="city" value={f.city} onChange={ch}/></label>
      <label>How can we help?<textarea data-testid="pc-needs" required minLength={5} name="needs" value={f.needs} onChange={ch} placeholder="Tell us what you need managed, found or done"/></label>
      <button data-testid="pc-submit" className="coral-button full" type="submit" disabled={busy}><PhoneCall size={15}/> {busy ? "Sending..." : "Request a call back"}</button>
    </form>
  );
}

function ConciergeTab({notify}) {
  const t = useSite();
  return (
    <section className="concierge-tab" data-testid="public-concierge">
      <div className="ct-intro">
        <div className="eyebrow"><Globe2 size={11}/> FOR EVERYONE, EVERYWHERE</div>
        <h1><Rich text={t("gfi_title")}/></h1>
        <p className="ct-sub">{t("gfi_sub")}</p>
        <p className="ct-purpose">{t("gfi_purpose")}</p>
        <div className="ct-help">
          {HELP.map(([Icon, title, d]) => <div key={title}><span><Icon size={17}/></span><strong>{title}</strong><small>{d}</small></div>)}
        </div>
      </div>
      <ConciergeForm notify={notify}/>
    </section>
  );
}

function CommunityTab({setUser, notify}) {
  const t = useSite();
  return (
    <div className="welcome" data-testid="welcome-community">
      <section className="welcome-hero">
        <div className="eyebrow">CULTURE · COMMUNITY · COMMERCE</div>
        <h1><Rich text={t("welcome_title")}/></h1>
        <p>{t("welcome_sub")}</p>
        <div className="welcome-points">
          {POINTS.map(([Icon, ti, d]) => <div key={ti}><span><Icon size={17}/></span><strong>{ti}</strong><small>{d}</small></div>)}
        </div>
      </section>
      <section className="welcome-auth">
        <AuthModal inline close={() => {}} setUser={setUser} notify={notify}/>
      </section>
    </div>
  );
}

export default function Welcome({setUser, notify}) {
  const [tab, setTabRaw] = useState(() => new URLSearchParams(window.location.search).get("tab") === "concierge" ? "concierge" : "community");
  const setTab = (x) => { setTabRaw(x); window.history.replaceState(null, "", x === "concierge" ? "/?tab=concierge" : "/"); window.scrollTo(0, 0); };
  return (
    <div className={`welcome-page wt-${tab}`} data-testid="welcome-page">
      <header className="welcome-bar">
        <Logo/>
        <div className="welcome-switch" role="tablist" data-testid="welcome-tabs">
          <button role="tab" aria-selected={tab === "community"} data-testid="welcome-tab-community" className={tab === "community" ? "active" : ""} onClick={() => setTab("community")}><Users size={14}/> Indian Ocean</button>
          <button role="tab" aria-selected={tab === "concierge"} data-testid="welcome-tab-concierge" className={tab === "concierge" ? "active" : ""} onClick={() => setTab("concierge")}><Package size={14}/> Personal Concierge</button>
          <span className="ws-pill" style={{transform: tab === "concierge" ? "translateX(100%)" : "none"}}/>
        </div>
      </header>
      {tab === "concierge" ? <ConciergeTab notify={notify}/> : <CommunityTab setUser={setUser} notify={notify}/>}
    </div>
  );
}
